import React, { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  activeView,
  auctionBidders,
  auctionGet,
} from "../../../Redux/Slices/auctionSlice";

import Styles from "./RfpMainScreen.module.css";

import AddIcon from "@mui/icons-material/Add";
import SelfListingSkeleton from "../../../commons/AvailableListingSkeleton";
import emptySelfListing from "../../../assets/images/emptySelfListing.png";
import RFPTender from "../RFPTender/RFPTender";
import CreateRfpTender from "../CreateRfpTender/CreateRfpTender";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import { Add } from "@mui/icons-material";
import emptyAvailableListing from "../../../assets/images/emptyAvailableListing.svg";
import { useLocation } from "react-router-dom";

// 1. CONFIGURATION: Extract static data outside component to prevent re-creation
const NOTIFICATION_TRIGGERS = [
  "BSRANotifyInvitedGenerator",
  "BSRANotifyConsumerTenderCreated",
  "BSRANotifyConsumerTenderTerminated",
  "BSRANotifyConsumerTenderUpdate",
  "BSRANotifyConsumerBidCreated",
  "BSRANotifyBidWinnerGenerator",
  "BSRANotifyBidRejectedGenerator",
  "BSRANotifyAmountChangeRequestGenerator",
  "BSRANotifyAmountChangeResponseConsumer",
  "BSRANotifyConsumerBidUpdated",
  "BSRANotifyBidWinnerGeneratorStageTwo",
  "BSRANotifyBidWinnerGeneratorStageOne",
  "BSRANotifyBidRejectedGeneratorStageOne",
  "BSRANotifyBidRejectedGeneratorStageTwo",
  "BSRANotifyGeneratorBidCreated",
  "BSRANotifyConsumerTenderActivation",
  "BSRANotifyGeneratorBidUpdated",
  "BSRANotifyConsumerTenderStart",
  "BSRANotifyConsumerTenderEnd",
  "BSRANotifyGeneratorTenderStart",
  "BSRANotifyGeneratorTenderEnd",
  "BSRANotifyConsumerBidStart",
  "BSRANotifyConsumerBidEnd",
  "BSRANotifyGeneratorBidStart",
  "BSRANotifyGeneratorBidEnd",
  "BSRANotifyConsumerStartAuction",
  "BSRANotifyConsumerEndAuction",
  "BSRANotifyGeneratorStartAuction",
  "BSRANotifyGeneratorEndAuction",
  "BSRANotifyTenderActivationAllGenerator",
  "BSRANotifyAmountChangeRequestConsumer",
  "BSRANotifyAmountChangeResponseGenerator",
  "BSRANotifyBidWinnerConsumer",
  "BSRAEmdBlockedNotification",
  "BSRAEmdReleasedNotification",
  "BSRAConsumerRaFeeBlockedNotification",
  "BSRAGenerateConsent",
  "BSRFQNotifyConsumerTenderCreated",
  "BSRANotifyLOIUploaded",
  "BSRAAgreementUploaded",
  "BSRAScheduleGenerate",
  "BSRANotifyMakerChecker",
];

const VIEW_FILTERS = [
  {
    name: "All",
    value: "ALL",
  },
  {
    name: "Drafted",
    value: "Drafted",
  },
  {
    name: "Bidding",
    value: "IPO",
  },
  {
    name: "Documentation",
    value: "TERMS",
  },
  {
    name: "Ongoing Contract",
    value: "CLOSED",
  },
];

const RfpMainScreen = () => {
  const dispatch = useDispatch();
  const auctionList = useSelector((state) => state?.auctionSlice?.auctionGet);
  const role = useSelector((state) =>
    (state?.persistedReducer?.user?.activeRole || "").toLowerCase(),
  );
  const state = useSelector(
    (store) => store?.persistedReducer?.company?.stateLabel,
  );
  const loacation = useLocation();
  const url = loacation?.pathname || "";
  const isTradeConnect = url === "/tradeconnect";
  const isRfq = url === "/rfq";

  const viewType = isTradeConnect ? "CLOSED" : state === "OD" ? "OPEN" : null;

  const notify = useSelector((state) => state?.notificationSlice?.latest);
  const activeState = useSelector((state) => state?.auctionSlice?.activeView);

  const [page, setPage] = useState(0);
  const [expandedTender, setExpandendTender] = useState("");
  const [createModal, setCreateModal] = useState(false);

  // 3. LOGIC: Helper to get the correct API section parameter
  const getApiSection = useCallback((viewState) => {
    return VIEW_FILTERS.find((f) => f.name === viewState)?.value || "All";
  }, []);

  const fetchAuctionData = useCallback(() => {
    const payload = {
      section: getApiSection(activeState),
      tenderType: "CB",
      page,
      auctionType: viewType && `auctionType=${viewType}`,
    };

    if (role === "consumer") {
      dispatch(auctionGet(payload));
    } else if (role === "generator") {
      dispatch(auctionBidders(payload));
    }
  }, [activeState, role, page, dispatch, getApiSection, viewType]);

  // Re-fetch on activeState, role, or page change
  useEffect(() => {
    if (!activeState) {
      dispatch(activeView("All"));
      return;
    }
    fetchAuctionData();
  }, [activeState, role, page, dispatch, fetchAuctionData, viewType]);

  // Notification listener
  useEffect(() => {
    if (!notify?.key || !NOTIFICATION_TRIGGERS.includes(notify.key)) return;

    if (role === "consumer") {
      fetchAuctionData();
    } else {
      setTimeout(() => fetchAuctionData(), 2000);
    }
  }, [notify, fetchAuctionData, role]);

  const handleExpandedTender = (val) => {
    setExpandendTender(val);
  };

  const ContainerHeader = () => {
    return (
      <>
        {createModal && (
          <CreateRfpTender
            open={createModal}
            handleClose={() => setCreateModal(false)}
            duplicate={null}
            editData={null}
            type={"create"}
            priceDiscoveryType={"CB"}
          />
        )}
        <div className={Styles.containerHeader}>
          <div className={Styles.containerHeaderLeftCont}>
            {role === "consumer" ? (
              <>
                <div className={Styles.tenderHeading}>My Tenders</div>
                <div
                  className={Styles.addTenderButton}
                  onClick={() => setCreateModal(true)}
                >
                  <AddIcon style={{ height: "16px", width: "16px" }} />
                  <span>Add Tender</span>
                </div>
              </>
            ) : (
              <>
                <div className={Styles.tenderHeading}>
                  Tender List: <span>{auctionList?.length || 0}</span>
                </div>
              </>
            )}
          </div>
          <div className={Styles.filterSectionMain}>
            {VIEW_FILTERS?.filter(
              (ele) => !(role === "generator" && ele.value === "Drafted"),
            ).map((ele) => (
              <div
                key={ele.value}
                className={
                  activeState === ele.name
                    ? Styles.filterButtonActive
                    : Styles.filterButtonInactive
                }
                onClick={() => dispatch(activeView(ele.name))}
              >
                {ele?.name}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const PaginationContainer = () => {
    const isPrevDisabled = page === 0;
    const isNextDisabled = (auctionList?.length ?? 0) < 100;

    return (
      <div className={Styles.paginationContainer}>
        <div
          className={
            isPrevDisabled
              ? Styles.paginationButtonDisabled
              : Styles.filterButtonInactive
          }
          onClick={isPrevDisabled ? undefined : () => setPage((p) => p - 1)}
        >
          Prev
        </div>
        <div
          className={
            isNextDisabled
              ? Styles.paginationButtonDisabled
              : Styles.filterButtonInactive
          }
          onClick={isNextDisabled ? undefined : () => setPage((p) => p + 1)}
        >
          Next
        </div>
      </div>
    );
  };

  const DisplayListContainer = () => {
    if (auctionList === null) {
      return (
        <div className={Styles.displayTenderContainer}>
          <SelfListingSkeleton count={4} />
        </div>
      );
    }

    if (auctionList.length === 0) {
      return (
        <div className={Styles.emptyState}>
          <img src={emptyAvailableListing} alt="No Tender" />
          <div style={{ color: "var(--primary-color-text)" }}>
            {role === "consumer" ? (
              <span>
                You have not listed your <b>requirements</b>
              </span>
            ) : (
              <span>
                No Active <b>requirements</b>
              </span>
            )}
          </div>
          {role === "consumer" && (
            <div
              className={`activeButton_type_2 ${Styles.addButton}`}
              onClick={() => setCreateModal(true)}
            >
              <Add style={{ fontSize: "16px" }} />
              <span>Add Tender</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`scroll_bar_y ${Styles.displayTenderContainer}`}>
        {auctionList.map((tender) => (
          <RFPTender
            key={tender.id}
            data={tender}
            expandedTender={expandedTender}
            handleExpandedTender={handleExpandedTender}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={Styles.rfpMainContainer}>
      {ContainerHeader()}
      {DisplayListContainer()}
      {PaginationContainer()}
    </div>
  );
};

export default RfpMainScreen;
